/** Small presentational pieces of the prototype's visual language. */
import { ProgressSpinner } from 'primereact/progressspinner';
import { type ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

import { SHARED_COMPONENTS_TEXT } from '../constants';

export type Tone = 'good' | 'warn' | 'crit' | 'info' | 'idle' | 'accent';

const PILL: Record<Tone, string> = {
  good: 'bg-good-soft text-good border-good-line',
  warn: 'bg-warn-soft text-warn border-warn-line',
  crit: 'bg-crit-soft text-crit border-crit-line',
  info: 'bg-info-soft text-info border-info-line',
  idle: 'bg-idle-soft text-idle border-idle-line',
  accent: 'bg-accent-soft text-accent border-accent-soft',
};

/** Status pill with a leading dot (overdue, active, open, …). */
export function BasePill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.25 py-0.5 text-[11.5px] font-semibold whitespace-nowrap',
        PILL[tone],
      )}
    >
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {children}
    </span>
  );
}

/** Initials bubble used for people. */
export function BaseAvatar({ initials, title }: { initials: string; title?: string }) {
  return (
    <span
      title={title}
      className="grid size-6 flex-none place-items-center rounded-full bg-accent-soft text-[9.5px] font-semibold tracking-tight text-accent"
    >
      {initials}
    </span>
  );
}

const MESSAGE: Record<'error' | 'info' | 'success' | 'warn', string> = {
  error: 'border-crit-line bg-crit-soft text-crit',
  info: 'border-info-line bg-info-soft text-info',
  success: 'border-good-line bg-good-soft text-good',
  warn: 'border-warn-line bg-warn-soft text-warn',
};

/** Inline callout (form-level errors, notices). */
export function BaseMessage({
  tone,
  children,
}: {
  tone: 'error' | 'info' | 'success' | 'warn';
  children: ReactNode;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('rounded-app border px-3.5 py-2.5 text-[12.5px] normal-case', MESSAGE[tone])}
    >
      {children}
    </div>
  );
}

/** Bordered surface with an optional header row (prototype `.panel`). */
export function BasePanel({
  title,
  actions,
  actionsFill,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  /** The actions take the whole header row, so `ml-auto` inside them can push items right. */
  actionsFill?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-app border border-line bg-surface', className)}>
      {(title ?? actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2.5 border-b border-line px-3.5 py-3">
          {title && <h2 className="text-sm font-semibold">{title}</h2>}
          {actions && (
            <div className={cn('flex flex-wrap items-center gap-2', actionsFill && 'flex-1')}>{actions}</div>
          )}
        </header>
      )}
      {children}
    </section>
  );
}

export function BaseSpinner({ label = SHARED_COMPONENTS_TEXT.spinner.loading }: { label?: string }) {
  return (
    <div role="status" aria-label={label} className="grid place-items-center p-6">
      <ProgressSpinner style={{ width: 32, height: 32 }} strokeWidth="4" />
    </div>
  );
}
